/**
 * Interface language.
 *
 * Mirrors themeStore: the choice is persisted, everything else is derived.
 * Russian is the default because that is what the platform shipped with —
 * a returning user who never touches the picker must see no change.
 *
 * `<html lang>` is written here too. Nothing in this app reads it, but the
 * browser does: it picks hyphenation and, more importantly for us, which font
 * to substitute for a glyph the loaded face is missing.
 */
import { create } from "zustand";

export const LOCALES = ["ru", "kk", "en"] as const;
export type Locale = (typeof LOCALES)[number];

const STORAGE_KEY = "kgs.locale";
const DEFAULT_LOCALE: Locale = "ru";

/**
 * Which locale's *conventions* Intl should use for dates and numbers. Not the
 * same thing as the interface language.
 *
 * Kazakh maps to ru-RU on purpose. Chrome reports `kk` as supported, but the
 * data behind it is ICU's root locale, not Kazakh: `dateStyle: "medium"` comes
 * back as "2026 M01 5", the group separator is a comma and the decimal mark is
 * a dot. Kazakhstan writes 05.01.2026, groups with a space and marks decimals
 * with a comma — identical to Russian — so ru-RU produces correct output where
 * kk-KZ silently produces root-locale output.
 *
 * This affects formatting only. Plural rules keep using the real language code,
 * and every visible word still comes from the Kazakh dictionary.
 */
export const FORMAT_LOCALE: Record<Locale, string> = {
  ru: "ru-RU",
  kk: "ru-RU",
  en: "en-US",
};

function isLocale(value: unknown): value is Locale {
  return LOCALES.includes(value as Locale);
}

function read(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isLocale(stored) ? stored : DEFAULT_LOCALE;
}

function applyToDom(locale: Locale): void {
  document.documentElement.setAttribute("lang", locale);
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>((set) => {
  const locale = read();
  applyToDom(locale);

  return {
    locale,
    setLocale: (next) => {
      localStorage.setItem(STORAGE_KEY, next);
      applyToDom(next);
      set({ locale: next });
    },
  };
});
